import { useEffect, useState } from 'react'


const PHASE = {
    BLINK: 'blink',       // El cursor parpadea, todavía sin texto
    TYPING: 'typing',     // Se escribe el texto y el cursor avanza
    HOLD: 'hold',         // El texto completo se mantiene un tiempo
    DELETING: 'deleting'  // El cursor regresa borrando el texto
}

/**
 * Texto con efecto de máquina de escribir: el cursor parpadea, se escribe la frase mientras el cursor
 * avanza, la frase se mantiene un tiempo y el cursor regresa borrándola; después sigue la siguiente frase
 * (y al terminar la última vuelve a la primera).
 *
 * La frase completa ocupa su lugar desde el principio (se reserva con una copia invisible), así que el
 * texto queda centrado y no se mueve mientras se escribe. El alto se reserva con la frase más larga, para
 * que el contenido de abajo no brinque al cambiar de frase.
 *
 * @param {string[]} phrases - Frases que se escriben, una tras otra.
 * @param {number} blinkMs - Tiempo que parpadea el cursor antes de escribir cada frase.
 * @param {number} typeMs - Tiempo entre una letra y la siguiente al escribir.
 * @param {number} holdMs - Tiempo que se mantiene la frase completa.
 * @param {number} deleteMs - Tiempo entre una letra y la siguiente al borrar.
 * @param {string} extraClassName - Clases adicionales para el contenedor.
 */
const TypewriterText = ({ phrases = [], blinkMs = 1200, typeMs = 70, holdMs = 3000, deleteMs = 35, extraClassName = '' }) => {
    const [phraseIndex, setPhraseIndex] = useState(0)
    const [typedLength, setTypedLength] = useState(0)
    const [phase, setPhase] = useState(PHASE.BLINK)

    const phrase = phrases[phraseIndex] ?? ''

    useEffect(() => {
        if (phrases.length === 0) return undefined

        let timer

        if (phase === PHASE.BLINK) {
            timer = setTimeout(() => setPhase(PHASE.TYPING), blinkMs)
        } else if (phase === PHASE.TYPING) {
            if (typedLength < phrase.length) {
                timer = setTimeout(() => setTypedLength((length) => length + 1), typeMs)
            } else {
                setPhase(PHASE.HOLD)
            }
        } else if (phase === PHASE.HOLD) {
            timer = setTimeout(() => setPhase(PHASE.DELETING), holdMs)
        } else if (phase === PHASE.DELETING) {
            if (typedLength > 0) {
                timer = setTimeout(() => setTypedLength((length) => length - 1), deleteMs)
            } else {
                setPhraseIndex((index) => (index + 1) % phrases.length)
                setPhase(PHASE.BLINK)
            }
        }

        return () => clearTimeout(timer)
    }, [phase, typedLength, phrase, phrases.length, blinkMs, typeMs, holdMs, deleteMs])

    if (phrases.length === 0) return null

    const shownText = phrase.slice(0, typedLength)
    const isCursorBlinking = phase === PHASE.BLINK || phase === PHASE.HOLD

    return (
        <div className={`Typewriter ${extraClassName}`.trim()}>
            {/* Copias invisibles de todas las frases: reservan el alto de la más larga */}
            {phrases.map((text) => (
                <span key={text} className='Typewriter__Height' aria-hidden='true'>{text}</span>
            ))}

            <span className='Typewriter__Line'>
                {/* Copia invisible de la frase actual: reserva su ancho para que quede centrada */}
                <span className='Typewriter__Width' aria-hidden='true'>{phrase}</span>

                <span className='Typewriter__Typed' aria-hidden='true'>
                    {shownText}
                    <span className={`Typewriter__Cursor ${isCursorBlinking ? 'Typewriter__Cursor--Blink' : ''}`.trim()}></span>
                </span>

                {/* Los lectores de pantalla leen la frase completa, no letra por letra */}
                <span className='Typewriter__ScreenReader'>{phrase}</span>
            </span>
        </div>
    )
}

export { TypewriterText }
